import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FileTextIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { BillAuditLogTable } from "@/components/BillAuditLogTable";
import { BillFileDrawer } from "@/components/BillFileDrawer";
import { BillAllocationsTable } from "@/components/BillAllocationsTable";
import { BillLlmExtractionCard } from "@/components/BillLlmExtractionCard";
import { ConfirmActionDialog } from "@/components/ConfirmActionDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency, formatDateTime, formatNumber } from "@/lib/format";

type Bill = components["schemas"]["BillResponse"];
type BillLineItem = components["schemas"]["BillLineItemResponse"];

function stateVariant(state: string | undefined): "default" | "secondary" | "outline" | "destructive" {
  switch (state) {
    case "Paid":
      return "default";
    case "Approved":
    case "Scheduled":
      return "secondary";
    case "Rejected":
      return "destructive";
    default:
      return "outline";
  }
}

export function BillPage() {
  const { billId } = useParams<{ billId: string }>();

  const [bill, setBill] = useState<Bill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inCompanyInbox, setInCompanyInbox] = useState(false);
  const [fileDrawerOpen, setFileDrawerOpen] = useState(false);

  useEffect(() => {
    if (!billId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function fetchBill() {
      setLoading(true);
      setInCompanyInbox(false);
      const { data, error: apiError, response } = await api.GET("/admin/bills/{id}", {
        params: { path: { id: billId! } },
      });
      if (cancelled) return;
      if (apiError && response?.status === 404) {
        // A company-inbox bill isn't in the Bills table until routed; forward rather than dead-end.
        const inbox = await api.GET("/admin/company-inbox-bills/{billId}", {
          params: { path: { billId: billId! } },
        });
        if (cancelled) return;
        if (inbox.data) setInCompanyInbox(true);
        else setBill(null);
      } else if (apiError) {
        setError("Failed to load bill.");
      } else {
        setBill(data ?? null);
      }
      setLoading(false);
    }

    fetchBill();
    return () => {
      cancelled = true;
    };
  }, [billId]);

  if (inCompanyInbox && billId) return <Navigate to={`/company-inbox-bills/${billId}`} replace />;

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  const currency = bill?.currency ?? "GBP";
  const supplierLink = bill?.supplierId ? `/suppliers/${bill.supplierId}` : null;

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Bill</h1>
        {bill && (
          <Button variant="outline" onClick={() => setFileDrawerOpen(true)}>
            <FileTextIcon className="size-4" />
            View file
          </Button>
        )}
      </div>

      {bill?.id && (
        <BillFileDrawer
          billId={bill.id}
          fileName={bill.fileName}
          open={fileDrawerOpen}
          onOpenChange={setFileDrawerOpen}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : bill ? (
        <>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>
                {bill.invoiceNumber || <span className="text-muted-foreground">Unnumbered</span>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Section title="Bill">
                <Field label="Bill ID">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {bill.id}
                  </Badge>
                </Field>
                <Field label="Organisation">
                  {bill.organisationId ? (
                    <Link to={`/organisations/${bill.organisationId}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {bill.organisationId}
                      </Badge>
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Field>
                <Field label="State">
                  <Badge variant={stateVariant(bill.state)}>{bill.state}</Badge>
                </Field>
                <Field label="Source">{bill.source ?? "—"}</Field>
                <Field label="Extraction confidence">
                  {bill.confidence != null ? (
                    <Badge className="border-transparent bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                      {Math.round(Number(bill.confidence) * 100)}%
                    </Badge>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Currency">{bill.currency}</Field>
                <Field label="Extracted PO numbers">
                  {bill.billPoNumbers?.length ? bill.billPoNumbers.join(", ") : "—"}
                </Field>
                <Field label="Normalised PO numbers">
                  {bill.normalisedBillPoNumbers?.length ? (
                    <span className="font-mono text-xs">{bill.normalisedBillPoNumbers.join(", ")}</span>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Invoice date">{formatDateTime(bill.invoiceDate)}</Field>
                <Field label="Due date">{formatDateTime(bill.dueDate)}</Field>
                <Field label="File name">{bill.fileName ?? "—"}</Field>
              </Section>

              <Section title="Supplier">
                <Field label="Name">
                  {supplierLink ? (
                    <Link to={supplierLink} className="hover:underline">
                      {bill.supplier?.name ?? bill.supplierName ?? "—"}
                    </Link>
                  ) : (
                    bill.supplier?.name ?? bill.supplierName ?? "—"
                  )}
                </Field>
                <Field label="Supplier ID">
                  {bill.supplierId ? (
                    <Link to={`/suppliers/${bill.supplierId}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {bill.supplierId}
                      </Badge>
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Field>
                <Field label="Suggested match">
                  {bill.suggestedMatchedSupplierId ? (
                    <Link to={`/suppliers/${bill.suggestedMatchedSupplierId}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {bill.suggestedMatchedSupplierId.slice(0, 8)}...
                      </Badge>
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Field>
                <Field label="Email">{bill.supplierEmail ?? "—"}</Field>
                <Field label="Phone">{bill.supplierPhone ?? "—"}</Field>
                <Field label="Tax ID">{bill.supplierTaxId ?? "—"}</Field>
                <Field label="Address" className="sm:col-span-3">
                  {bill.supplierAddress ?? "—"}
                </Field>
              </Section>

              <Section title="Amounts">
                <Field label="Subtotal">{formatCurrency(bill.subTotal, currency)}</Field>
                <Field label="Tax">{formatCurrency(bill.taxAmount, currency)}</Field>
                <Field label="Total">
                  <span className="font-semibold">
                    {formatCurrency(bill.totalAmount, currency)}
                  </span>
                </Field>
              </Section>

              <Section title="Payment">
                <Field label="Terms">{bill.paymentTerms ?? "—"}</Field>
                <Field label="Reference">{bill.paymentRef ?? "—"}</Field>
                <Field label="Method">{bill.paymentMethod ?? "—"}</Field>
                <Field label="Scheduled">{formatDateTime(bill.paymentDate)}</Field>
                <Field label="Paid at">{formatDateTime(bill.paidAt)}</Field>
              </Section>

              {bill.duplicateOfBillId && (
                <Section title="Duplicate detection">
                  <Field label="Duplicate of">
                    <Link to={`/bills/${bill.duplicateOfBillId}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {bill.duplicateOfBillId}
                      </Badge>
                    </Link>
                  </Field>
                  <Field label="Detected">{formatDateTime(bill.duplicateDetectedAt)}</Field>
                  <Field label="Matched on">
                    {bill.duplicateMatchBasis ? (
                      <Badge
                        variant="secondary"
                        className="text-xs"
                        title={
                          bill.duplicateMatchBasis === "SupplierId"
                            ? "Both bills resolve to the same supplier record"
                            : "Neither bill has a supplier on file; the extracted supplier names matched"
                        }
                      >
                        {bill.duplicateMatchBasis}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </Field>
                  {/* Blocks approval until dismissed, so the resolution state explains a stuck bill. */}
                  <Field label="Resolution">
                    {bill.duplicateDismissedAt ? (
                      <Badge className="border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                        Confirmed not a duplicate
                      </Badge>
                    ) : (
                      <Badge className="border-transparent bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                        Unresolved — approval blocked
                      </Badge>
                    )}
                  </Field>
                  <Field label="Confirmed at">{formatDateTime(bill.duplicateDismissedAt)}</Field>
                </Section>
              )}

              {(bill.approvedAt || bill.rejectedAt) && (
                <Section title="Review">
                  <Field label="Approved">{formatDateTime(bill.approvedAt)}</Field>
                  <Field label="Approved by">
                    <ActorLink actorId={bill.approvedBy} />
                  </Field>
                  <Field label="Rejected">{formatDateTime(bill.rejectedAt)}</Field>
                  <Field label="Rejected by">
                    <ActorLink actorId={bill.rejectedBy} />
                  </Field>
                  {bill.rejectionReason && (
                    <Field label="Rejection reason" className="sm:col-span-3">
                      {bill.rejectionReason}
                    </Field>
                  )}
                </Section>
              )}

              <Section title="Audit">
                <Field label="Created">{formatDateTime(bill.createdAt)}</Field>
                <Field label="Created by">
                  <ActorLink actorId={bill.createdBy} />
                </Field>
                <Field label="Updated">{formatDateTime(bill.updatedAt)}</Field>
                <Field label="Updated by">
                  <ActorLink actorId={bill.updatedBy} />
                </Field>
              </Section>
            </CardContent>
          </Card>

          <BillLlmExtractionCard billId={bill.id!} />

          <h2 className="mb-4 mt-8 text-lg font-semibold">Line items</h2>
          <LineItemsTable items={bill.lineItems ?? []} currency={currency} />

          <BillMatchingSection billId={bill.id!} voided={bill.state === "Voided"} />

          <h2 className="mb-4 mt-8 text-lg font-semibold">Audit log</h2>
          <BillAuditLogTable billId={bill.id!} />
        </>
      ) : (
        <p className="text-muted-foreground">Bill not found.</p>
      )}
    </div>
  );
}

// Lazy: allocations are fetched only when opened. "Clear matches" is for voided bills only (the endpoint refuses
// others): bills voided before voiding dropped match data still hold PO/DN lines.
function BillMatchingSection({ billId, voided }: { billId: string; voided: boolean }) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  // Bumped after a clear so the table remounts and re-fetches.
  const [reloadKey, setReloadKey] = useState(0);

  async function clearMatches() {
    setClearing(true);
    const { data, error } = await api.POST("/admin/bills/{billId}/clear-matches", {
      params: { path: { billId } },
    });
    setClearing(false);

    if (error || !data) {
      const detail = (error as { detail?: string } | undefined)?.detail ?? "Failed to clear matches.";
      toast.error(detail);
      return;
    }

    setConfirmOpen(false);
    setOpen(true);
    setReloadKey((k) => k + 1);
    const removed = Number(data.allocationsRemoved);
    toast.success(
      removed > 0
        ? `Cleared ${removed} allocation${removed === 1 ? "" : "s"}.`
        : "Nothing to clear - this bill had no match data.",
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-8">
      <div className="flex items-center gap-4">
        <CollapsibleTrigger className="flex flex-1 items-center gap-2 text-lg font-semibold hover:text-primary">
          {open ? (
            <ChevronDownIcon className="size-5" />
          ) : (
            <ChevronRightIcon className="size-5" />
          )}
          Match Allocations
        </CollapsibleTrigger>
        {voided && (
          <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            Clear matches
          </Button>
        )}
      </div>
      <CollapsibleContent className="mt-4">
        {open && <BillAllocationsTable key={reloadKey} billId={billId} />}
      </CollapsibleContent>
      <ConfirmActionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Clear this bill's matches?"
        description="Deletes the voided bill's match group and allocations, and frees the purchase-order and delivery-note lines it was holding so a re-issued invoice can be matched. This can't be undone."
        confirmLabel="Clear matches"
        pending={clearing}
        onConfirm={clearMatches}
      />
    </Collapsible>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function ActorLink({ actorId }: { actorId: string | null | undefined }) {
  if (!actorId) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/users/${actorId}`}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {actorId.slice(0, 8)}...
      </Badge>
    </Link>
  );
}

function LineItemsTable({ items, currency }: { items: BillLineItem[]; currency: string }) {
  // #line-{billLineId} is set by the match drill-through. Read once on mount: re-reading on every hash
  // change would fight the browser's own anchor handling.
  const [highlightLineId] = useState(() =>
    typeof window === "undefined" ? null : window.location.hash.replace(/^#line-/, "") || null,
  );
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [items]);

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead className="text-right">Unit price</TableHead>
            <TableHead className="text-right">Tax rate</TableHead>
            <TableHead className="text-right">Tax</TableHead>
            <TableHead className="text-right">Subtotal</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground h-24 text-center">
                No line items.
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow
                key={item.id}
                id={`line-${item.id}`}
                // Ring and scroll to the line the match drill-through pointed at.
                ref={item.id === highlightLineId ? highlightRef : undefined}
                className={item.id === highlightLineId ? "bg-amber-500/10 ring-1 ring-inset ring-amber-500/40" : undefined}
              >
                <TableCell className="font-medium">{item.description}</TableCell>
                <TableCell className="font-mono text-xs">{item.sku ?? "-"}</TableCell>
                <TableCell className="text-right">{formatNumber(item.quantity)}</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(item.unitPrice, currency)}
                </TableCell>
                <TableCell className="text-right">
                  {item.taxRate != null ? `${formatNumber(item.taxRate)}%` : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(item.taxAmount, currency)}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(item.subTotal, currency)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
